'use client';

import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { Tooltip } from 'src/components';
import { twMerge } from 'tailwind-merge';
import { LICENSE_COPY, STAT_TOOLTIP_DELAY_MS } from '../../constants';
import { getLicenseSummary } from '../../utils';

interface LicenseOverviewProps {
  company?: MedScout.Company | null;
}

/**
 * `primary` is the headline figure; `warning` calls out a figure that is
 * blocking something — currently only having no seats left.
 */
type StatCardTone = 'neutral' | 'primary' | 'warning';

interface StatCardProps {
  label: string;
  /**
   * What the figure means. Revealed by hovering the info icon beside `label`,
   * which is what advertises that there is anything to hover at all (MKT-526).
   */
  tooltip: string;
  value: React.ReactNode;
  /** Only for figures that carry extra data, not for describing the card. */
  detail?: string;
  /**
   * A second line under `detail`, for an action to take rather than a figure.
   * Kept separate so the numbers never have to give up their spot to it.
   */
  note?: string;
  /** Larger and tinted for `primary`; amber for `warning`. */
  tone?: StatCardTone;
  'data-testid': string;
}

const TONE_CLASSES: Record<
  StatCardTone,
  { card: string; value: string; detail: string }
> = {
  neutral: {
    card: 'border-neutral-200',
    value: 'text-2xl text-neutral-900',
    detail: 'text-neutral-600',
  },
  primary: {
    card: 'border-sky-200 bg-sky-50/40',
    value: 'text-3xl text-sky-700',
    detail: 'text-neutral-600',
  },
  warning: {
    card: 'border-amber-300 bg-amber-50',
    value: 'text-3xl text-amber-700',
    detail: 'text-amber-700',
  },
};

const StatCard: React.FC<StatCardProps> = ({
  label,
  tooltip,
  value,
  detail,
  note,
  tone = 'neutral',
  'data-testid': dataTestId,
}) => (
  <div
    className={twMerge(
      'flex flex-col gap-1 rounded-lg border bg-white p-4 shadow-sm',
      TONE_CLASSES[tone].card
    )}
    data-testid={dataTestId}
  >
    <div className="flex w-fit items-center gap-1.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
        {label}
      </p>
      {/* The icon alone is the trigger: Tooltip centres itself on its trigger's
          bounding box, so wrapping the label too pulled the bubble off to the
          left of the icon it belongs to. */}
      <Tooltip
        content={tooltip}
        delay={STAT_TOOLTIP_DELAY_MS}
        slotProps={{ contentClassName: 'max-w-xs whitespace-normal text-left' }}
      >
        {/* The wrapper carries the label so assistive tech gets the explanation
            a sighted viewer only sees on hover; the glyph itself is decorative.
            Deliberately not `sr-only` text — that would put the explanation back
            into the card's text content, which is what MKT-382 took out. */}
        <span
          className="inline-flex"
          role="img"
          aria-label={tooltip}
          data-testid={`${dataTestId}-info-icon`}
        >
          <Icon
            icon={faCircleInfo}
            className="text-xs text-neutral-400 hover:text-neutral-600"
            aria-hidden="true"
          />
        </span>
      </Tooltip>
    </div>
    <p className={twMerge('font-semibold', TONE_CLASSES[tone].value)}>
      {value}
    </p>
    {detail && (
      <p className={twMerge('text-xs', TONE_CLASSES[tone].detail)}>{detail}</p>
    )}
    {note && (
      <p className={twMerge('text-xs', TONE_CLASSES[tone].detail)}>{note}</p>
    )}
  </div>
);

/**
 * @description License and headcount summary for a company. Active users is the
 *              headline figure; invited users are shown separately because they
 *              hold a license without appearing in an Active-filtered table.
 *              Every figure comes from the company payload's own counts, so the
 *              panel is accurate however many users the account has.
 * @returns React.ReactElement
 */
const LicenseOverview: React.FC<LicenseOverviewProps> = ({ company }) => {
  const {
    activeUsers,
    invitedUsers,
    totalLicenses,
    usedLicenses,
    availableLicenses,
    hasLicenseLimit,
  } = getLicenseSummary(company);

  // Being out of seats blocks adding anyone, so the card says so rather than
  // reading as an ordinary zero (MKT-383). The used/total line stays put
  // underneath it — that count is most worth stating when there is no room
  // left, and hiding it was the complaint in PTRI-169.
  const isAtSeatLimit = hasLicenseLimit && availableLicenses <= 0;

  return (
    <div
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
      data-testid="license-overview"
    >
      <StatCard
        tone="primary"
        label={LICENSE_COPY.activeLabel}
        tooltip={LICENSE_COPY.activeTooltip}
        value={activeUsers}
        data-testid="stat-active-users"
      />
      {/* Keeps its place in the row even when the count is unavailable, but
          shows a dash rather than a `0` that would read as "nobody is waiting
          to sign in" — a claim the payload does not support. */}
      <StatCard
        label={LICENSE_COPY.invitedLabel}
        tooltip={LICENSE_COPY.invitedTooltip}
        value={
          invitedUsers ?? (
            <>
              <span aria-hidden="true">{LICENSE_COPY.unknownCount}</span>
              <span className="sr-only">{LICENSE_COPY.unknownCountLabel}</span>
            </>
          )
        }
        data-testid="stat-invited-users"
      />
      <StatCard
        tone={isAtSeatLimit ? 'warning' : 'neutral'}
        label={LICENSE_COPY.availableLabel}
        tooltip={
          hasLicenseLimit
            ? LICENSE_COPY.availableTooltip
            : LICENSE_COPY.noLimitTooltip
        }
        value={hasLicenseLimit ? availableLicenses : LICENSE_COPY.noLimit}
        detail={
          hasLicenseLimit
            ? LICENSE_COPY.availableDetail(usedLicenses, totalLicenses)
            : undefined
        }
        note={isAtSeatLimit ? LICENSE_COPY.noSeatsDetail : undefined}
        data-testid="stat-available-seats"
      />
    </div>
  );
};

export default LicenseOverview;
